import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiCallsInProgress } from '@fg-services/api-calls-in-progress.service';
import { ApiService } from '@fg-services/api/api.service';
import { UiService } from '@fg-services/ui.service';
import { Survey, SurveyQuestion, SurveyType } from '@fg-types/survey';

@Component({
  selector: 'fg-ratings-criteria',
  templateUrl: './ratings.component.html',
  styleUrls: ['./ratings.component.scss']
})
export class RatingsComponent implements OnInit {
  conventionId: string;
  allSurveys: Survey[];

  constructor(
    private api: ApiService,
    public uiService: UiService,
    public apiCallsInProgress: ApiCallsInProgress,
    public route: ActivatedRoute
  ) {
    this.conventionId = route.snapshot.params.id;
  }

  ngOnInit() {
    this.loadSurveys();
  }

  loadSurveys() {
    this.apiCallsInProgress.addCall();
    this.api.survey
      .getSurveys('Convention', this.conventionId, SurveyType.ConventionRating)
      .subscribe(({ items }: { items: Survey[] }) => {
        this.allSurveys = items;
        this.apiCallsInProgress.reset();
      });
  }

  toggleSurvey({ isVisible, id }: Survey) {
    this.apiCallsInProgress.addCall();
    this.api.survey.toggleSurveyVisibility(id, { isVisible }).subscribe(
      () => {
        this.uiService.toast({
          severity: 'success',
          summary: `Survey is ${isVisible ? 'now' : 'no longer'} visible.`
        });
        this.apiCallsInProgress.reset();
      },
      err => {
        console.warn(err);
        this.uiService.toast({
          severity: 'error',
          summary: 'Error:',
          detail: 'There has been an error updating the survey.'
        });
        this.apiCallsInProgress.reset();
      }
    );
  }

  updateQuestion(surveyId: string, { id, isVisible, name }: Partial<SurveyQuestion>) {
    this.apiCallsInProgress.addCall();
    this.api.survey.editQuestion(surveyId, id, { isVisible, name }).subscribe(
      surveyResponse => {
        const allQuestionsHidden = surveyResponse.questions.every(question => !question.isVisible);
        if (allQuestionsHidden) {
          const surveyIndexToUpdate = this.allSurveys.findIndex(
            surv => surv.id === surveyResponse.id
          );
          this.allSurveys[surveyIndexToUpdate].isVisible = false;
          this.toggleSurvey(this.allSurveys[surveyIndexToUpdate]);
        }
        this.uiService.toast({
          severity: 'success',
          summary: `Question is ${isVisible ? 'now' : 'no longer'} visible.`
        });
        this.apiCallsInProgress.reset();
      },
      error => {
        let detail = 'There has been an error updating the question.';
        try {
          detail = error.error.error.developerMsg || error.error.error.message;
        } catch (e) {}
        this.uiService.toast({
          severity: 'error',
          summary: 'Error:',
          detail
        });
        this.apiCallsInProgress.reset();
        this.loadSurveys();
      }
    );
  }

  submitNewCriteria(survey: Survey) {
    this.apiCallsInProgress.addCall();
    this.api.survey
      .addQuestion(survey.id, {
        name: survey.newRatingCriteria,
        responseType: 'upvote',
        commentsEnabled: false
      })
      .subscribe(() => {
        this.loadSurveys();
        this.uiService.toast({
          severity: 'success',
          summary: 'New Criteria added'
        });
        this.apiCallsInProgress.reset();
      }, this.uiService.errorHandler);
    survey.newRatingCriteria = '';
  }

  deleteQuestion(survey: Survey, question: SurveyQuestion) {
    this.apiCallsInProgress.addCall();
    this.api.survey.deleteQuestionById(survey.id, question.id).subscribe(() => {
      this.loadSurveys();
      this.uiService.toast({
        severity: 'success',
        summary: 'Question deleted'
      });
    }, this.uiService.errorHandler);
  }
}
